import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import HelpDocLink, { HELP_DOC_URLS } from "../../HelpDocLink";
import { api } from "../../../api";
import useAbortableSearch from "../../../hooks/useAbortableSearch";
import { isSuccess } from "../../../api/httpStatus";
import {
  getDescendantATPIds,
  changeFieldEntityEntityList,
  getCuratorSourceId,
  setBiblioUpdatingEntityAdd,
  setEntityModalText,
  setTypeaheadName2CurieMap,
  updateButtonBiblioEntityAdd,
  setTopicEntitySourceId,
  setEditTag,
  biblioQueryReferenceCurie,
  fetchTaxonData,
  fetchDataContextData,
  changeBiblioActionToggler,
} from "../../../actions/biblioActions";
import { checkForExistingTags, setupEventListeners } from "./TopicEntityUtils";
import { defaultDataContext, hidesDataContextPulldown } from "./dataContextDefaults";

import Container from "react-bootstrap/Container";
import ModalGeneric from "../ModalGeneric";
import RowDivider from "../RowDivider";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { debounce } from 'lodash';
import Alert from "react-bootstrap/Alert";

// SCRUM-5697. data_context is a hierarchy under ATP:0000323, so the options come
// from the ontology (fetchDataContextData) rather than a literal list here.
// Only leaves are offered -- "mentioned data" and "marker data" are groupings,
// not curation choices. The default comes from defaultDataContext, which is
// per-MOD: WB topic tags take the ATP:0000323 root and everything else takes
// experimentally studied data.

// Labels for data_context terms that are NOT selectable but can still appear on a
// tag: the root and the two groupings. Pipelines write these (WB entity extraction
// sends the root), so the editor has to be able to display them.
const dataContextNameMap = {
  "ATP:0000323": "data context",
  "ATP:0000324": "mentioned data",
  "ATP:0000326": "marker data",
};

const TopicEntityCreate = () => {
  const debugMode = false;

  const dispatch = useDispatch();
  const runTopicSearch = useAbortableSearch();
  const runSpeciesSearch = useAbortableSearch();
  const editTag = useSelector((state) => state.biblio.editTag);
  const referenceJsonLive = useSelector((state) => state.biblio.referenceJsonLive);
  const referenceCurie = referenceJsonLive["curie"];
  const accessToken = useSelector((state) => state.isLogged.accessToken);
  const cognitoMod = useSelector((state) => state.isLogged.cognitoMod);
  const testerMod = useSelector((state) => state.isLogged.testerMod);
  const accessLevel = testerMod !== "No" ? testerMod : cognitoMod;
  const uid = useSelector((state) => state.isLogged.uid);

  const biblioUpdatingEntityAdd = useSelector((state) => state.biblio.biblioUpdatingEntityAdd);
  const entityModalText = useSelector((state) => state.biblio.entityModalText);
  const [topicSelectLoading, setTopicSelectLoading] = useState(false);
  const topicTypeaheadRef = useRef(null);
  const [typeaheadOptions, setTypeaheadOptions] = useState([]);
  const typeaheadName2CurieMap = useSelector((state) => state.biblio.typeaheadName2CurieMap);

  const topicEntitySourceId = useSelector((state) => state.biblio.topicEntitySourceId);

  const [speciesSelectLoading, setSpeciesSelectLoading] = useState(false);
  const speciesTypeaheadRef = useRef(null);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [isSpeciesSelected, setIsSpeciesSelected] = useState(false);
  const [geneDescendants, setGeneDescendants] = useState([]);
  const [alleleDescendants, setAlleleDescendants] = useState([]);

  const modToTaxon = useSelector((state) => state.biblio.modToTaxon || {});
  const curieToNameTaxon = useSelector((state) => state.biblio.curieToNameTaxon || {});
  const isFetchingTaxonData = useSelector((state) => state.biblio.isFetchingTaxonData);
  const [tagExistingMessage, setTagExistingMessage] = useState("");
  const [existingTagResponses, setExistingTagResponses] = useState([]);
  const [isTagExistingMessageVisible, setIsTagExistingMessageVisible] = useState(false);
  const [messageFailureSetCurationStatusToCurated, setMessageFailureSetCurationStatusToCurated] = useState("");
  const [isVisibleMessageFailureSetCurationStatusToCurated, setIsVisibleMessageFailureSetCurationStatusToCurated] = useState("");
  const [rows, setRows] = useState([
    { topicSelect: "", topicSelectValue: "", entityTypeSelect: "", taxonSelect: "", entityText: "", noteText: "", entityResultList: [],
      newDataCheckbox: false, newToDbCheckbox: false, newToFieldCheckbox: false, noDataCheckbox: false, entityAdditionDoneCheckbox: false,
      dataContextSelect: "" }
  ]);
  const [dataContextOptions, setDataContextOptions] = useState([]);
  const [topicEntityTags, setTopicEntityTags] = useState([]);
  const inputRefs = useRef([]);

  const [messages, setMessages] = useState([]);

  const [topicAtpToCurationStatus, setTopicAtpToCurationStatus] = useState({});

  const curieToNameMap = useMemo(
    () => Object.fromEntries(
      Object.entries(typeaheadName2CurieMap).map(([name, curie]) => [curie, name])
    ),
    [typeaheadName2CurieMap]
  );

  const taxonToMod = useMemo(() => {
    const result = {};
    for (const [mod, taxons] of Object.entries(modToTaxon || {})) {
      taxons.forEach((taxon) => {
        result[taxon] = mod;
      });
    }
    return result;
  }, [modToTaxon]);

  const warnTypesEntityValidation = ["no Alliance curie", "obsolete entity", "not found at WB", "no WB curie", "no SGD curie", "no mod curie"];

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchTaxonData());
    }
  }, [accessToken, dispatch]);

  const taxonList = useMemo(() => {
    const unsortedTaxonList = Object.values(modToTaxon || {}).flat();
    unsortedTaxonList.push("use_wb");
    unsortedTaxonList.push("NCBITaxon:9606");
    // Sort species by name, excluding empty string
    let sortedList = unsortedTaxonList.sort((a, b) => (curieToNameTaxon[a] > curieToNameTaxon[b] ? 1 : -1));

    // Reorder to put user's MOD species first
    if (accessLevel in (modToTaxon || {})) {
      const filteredTaxonList = sortedList.filter((x) => !modToTaxon[accessLevel].includes(x));
      sortedList = modToTaxon[accessLevel].concat(filteredTaxonList);
    }

    // Add "No species" option at the end
    sortedList.push("");

    return sortedList;
  }, [modToTaxon, curieToNameTaxon, accessLevel]);

  const unsortedTaxonListWB = [
    "",
    "NCBITaxon:135651",
    "NCBITaxon:6238",
    "NCBITaxon:6279",
    "NCBITaxon:281687",
    "NCBITaxon:6282",
    "NCBITaxon:54126",
    "NCBITaxon:31234",
    "NCBITaxon:34506",
    "NCBITaxon:70415",
  ];
  const curieToNameTaxonWB = {
    "NCBITaxon:135651": "Caenorhabditis brenneri",
    "NCBITaxon:6238": "Caenorhabditis briggsae",
    "NCBITaxon:6279": "Brugia malayi",
    "NCBITaxon:281687": "Caenorhabditis japonica",
    "NCBITaxon:6282": "Onchocerca volvulus",
    "NCBITaxon:54126": "Pristionchus pacificus",
    "NCBITaxon:31234": "Caenorhabditis remanei",
    "NCBITaxon:34506": "Strongyloides ratti",
    "NCBITaxon:70415": "Trichuris muris",
    "": "",
  };
  const taxonListWB = unsortedTaxonListWB.sort((a, b) => (curieToNameTaxonWB[a] > curieToNameTaxonWB[b] ? 1 : -1));

  const curieToNameEntityType = {
    "": "no value",
    "ATP:0000005": "gene",
    "ATP:0000006": "allele",
    "ATP:0000123": "species",
    "ATP:0000027": "strain",
    "ATP:0000025": "genotype",
    "ATP:0000026": "fish",
    "ATP:0000013": "transgenic construct",
    "ATP:0000110": "transgenic allele",
    "ATP:0000285": "classical allele",
    "ATP:0000093": "sequence targeting reagent"
  };
  const entityTypeList = [
    "",
    "ATP:0000005",
    "ATP:0000006",
    "ATP:0000110",
    "ATP:0000123",
    "ATP:0000285",
    "ATP:0000027",
    "ATP:0000025",
    "ATP:0000026",
    "ATP:0000013",
    "ATP:0000093"
  ];
  const speciesATP = "ATP:0000123";
  const renderView = (row) => {
    if (!row || !row.topicSelect) return "list";
    return row.topicSelect === speciesATP ? "autocomplete" : "list";
  };

  useEffect(() => {
    fetchDataContextData().then((data) => setDataContextOptions(data));
    getDescendantATPIds("ATP:0000005").then((data) => setGeneDescendants(data));
    getDescendantATPIds("ATP:0000006").then((data) => setAlleleDescendants(data));
  }, []);

  useEffect(() => {
    const fetchTopicEntityTags = async () => {
      try {
        const response = await api.get(`/topic_entity_tag/${editTag}`);
        console.log("TET response.data=", response.data)
        setTopicEntityTags(response.data);
      } catch (error) {
        console.error("Error fetching topic entity tags:", error);
      }
    };

    if (editTag !== null) {
      fetchTopicEntityTags();
    }
  }, [editTag]);

  useEffect(() => {
    console.log("useEffect triggered: editTag =", editTag);
    console.log("topicEntityTags =", topicEntityTags);
    if (editTag !== null && topicEntityTags) {
      const editRow = topicEntityTags;
      console.log("Found editRow:", editRow);
      if (editRow) {
        setRows([{
          topicSelect: editRow.topic || "",
	  topicSelectValue: curieToNameMap[editRow.topic] || "",
          entityTypeSelect: editRow.entity_type || "",
          taxonSelect: editRow.species || "",
          noDataCheckbox: editRow.negated || false,
          newDataCheckbox: editRow.data_novelty === 'ATP:0000321' ? true : false,
          newToDbCheckbox: editRow.data_novelty === 'ATP:0000228' ? true : false,
          newToFieldCheckbox: editRow.data_novelty === 'ATP:0000229' ? true : false,
          dataContextSelect: editRow.data_context || "",
	  confidence_score: editRow.confidence_score || null,
          confidence_level: editRow.confidence_level || false,
          entityText: editRow.entity_name || editRow.entity || "",
          noteText: editRow.note || "",
          entityResultList: editRow.entityResultList || []
        }]);
      }
    } else {
      setRows([createNewRow()]);
    }
  }, [editTag, topicEntityTags]);

  const addMessage = (text, variant) => {
    setMessages((prev) => [...prev, { text, variant }]);
  };

  const handleCloseMessage = (index) => {
    setMessages((prev) => prev.filter((_, idx) => idx !== index));
  };


  useEffect(() => {
    const fetchSourceId = async () => {
      if (accessToken !== null) {
        dispatch(setTopicEntitySourceId(await getCuratorSourceId(accessLevel, accessToken)));
      }
    };
    fetchSourceId().catch(console.error);
  }, [accessLevel, accessToken]);

  useEffect(() => {
    // Set default taxon for rows that don't have one, preserving manual selections
    if (modToTaxon && accessLevel in modToTaxon && modToTaxon[accessLevel].length > 0) {
      const defaultTaxon = modToTaxon[accessLevel][0];
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          taxonSelect: row.taxonSelect !== "" && row.taxonSelect !== undefined ? row.taxonSelect : defaultTaxon,
        }))
      );
    }
  }, [modToTaxon, accessLevel]);


  useEffect(() => {
    if (tagExistingMessage) {
      setupEventListeners(existingTagResponses, accessLevel, dispatch, updateButtonBiblioEntityAdd);
    }
  }, [tagExistingMessage, existingTagResponses, accessLevel]);

  useEffect(() => {
    const fetchCurationData = async () => {
      try {
        const result = await api.get(`/curation_status/aggregated_curation_status_and_tet_info/${referenceCurie}/${accessLevel}`);
        const processedCurationData = result.data.reduce((acc, info) => {
          acc[info.topic_curie] = {
            topic_name: info.topic_name,
            topic_curie: info.topic_curie,
            curation_status_id: info.curst_curation_status_id || 'new',
            curation_status: info.curst_curation_status || null,
          };
          return acc;
        }, {});
        console.log('processedCurationData');
        console.log(processedCurationData);
        setTopicAtpToCurationStatus(processedCurationData);
      } catch (error) {
        console.error('Error fetching curation data:', error);
      }
    };
    fetchCurationData();
  }, [referenceCurie, accessLevel]);

  const getMapKeyByValue = (mapObj, value) => {
    const objEntries = Object.entries(mapObj);
    const keyByValue = objEntries.filter((e) => e[1] === value);
    return keyByValue.map((e) => e[0])[0];
  };

  function initializeUpdateJson(refCurie, row, entityCurie, entityIdValidation, dataNoveltyAtp) {
    let json_data = {
	reference_curie: refCurie,
	topic: row.topicSelect || null,
	species: row.taxonSelect || null,
	note: row.noteText !== "" ? row.noteText : null,
	negated: row.noDataCheckbox || false,
	data_novelty: dataNoveltyAtp,
	// rowDataContext, not the entityCurie argument: patchEntities and the
	// topic-only branch of createEntities both pass entityCurie=null and attach
	// the entity afterwards, so keying off it saved the WB root for a row the
	// dropdown was showing as experimentally studied. Using the same helper the
	// <select> renders makes display and payload agree by construction.
	data_context: rowDataContext(row),
	confidence_score: null,
	confidence_level: null,
	tag_source_id: topicEntitySourceId || null
    }
    if (entityCurie) {
	json_data['entity_type'] = row.entityTypeSelect;
	json_data['entity'] = entityCurie;
        json_data['entity_id_validation'] = entityIdValidation;
    }
    return json_data;
  }


  const handleCloseTagExistingMessage = () => {
    setIsTagExistingMessageVisible(false);
    setTagExistingMessage("");
  };
  const handleCloseMessageFailureSetCurationStatusToCurated = () => {
    setIsVisibleMessageFailureSetCurationStatusToCurated(false);
    setMessageFailureSetCurationStatusToCurated("");
  }

  function createNewRow() {	
    return {
      topicSelect: "",
      topicSelectValue: "",
      entityTypeSelect: "",
      taxonSelect: modToTaxon[accessLevel] && modToTaxon[accessLevel][0] ? modToTaxon[accessLevel][0] : "",
      entityText: "",
      noteText: "",
      entityResultList: [],
      newDataCheckbox: false,
      newToDbCheckbox: false,
      newToFieldCheckbox: false,
      noDataCheckbox: false,
      entityAdditionDoneCheckbox: false,
      dataContextSelect: ""
    };
  }

  const handleEntityValidation = useCallback(
     debounce((index, value) => {
        setRows((prevRows) => {
            const newRows = [...prevRows]; // copy data
            const row = newRows[index];
            if (row.entityText === "") {
		console.log("Entity text is empty, resetting entityResultList");
                row.entityResultList = []; // reset entityResultList if entityText is empty
            } else if (
                row.taxonSelect !== "" &&
                row.taxonSelect !== undefined &&
                row.entityTypeSelect !== ""
            ) {
		// console.log("Validating entity:", row.entityText);
                let entityIdValidation = row.taxonSelect === "use_wb" ? "wb" : "alliance";
                // if (row.taxonSelect === "use_wb" && row.taxonSelectWB !== "" && row.taxonSelectWB !== undefined && row.entityTypeSelect !== "") {
                //    entityIdValidation = 'wb';
		//}
                dispatch(
                    changeFieldEntityEntityList(
                        row.entityText,
                        accessToken,
                        entityIdValidation,
                        row.taxonSelect,
                        curieToNameEntityType[row.entityTypeSelect],
                        (result) => {
                            setRows((updatedRows) => {
                                const finalRows = [...updatedRows];
                                if (inputRefs.current[index] === value) {
                                    finalRows[index].entityResultList = result;
                                }
                                return finalRows;
                            });
                        }
                    )
                );
            }
            return newRows;
        });
    }, 300),
    [rows, accessToken, dispatch, curieToNameEntityType]
  );

  const [tetDataLoading, setTetDataLoading] = useState(false);

  const handleTetDataClick = async (action) => {
    setTetDataLoading(true);

    const atpMap = {
      inProgress: "ATP:0000276",
      complete: "ATP:0000275",
    };

    try {
      const response = await api.post('/workflow_tag/transition_to_workflow_status', {
        curie_or_reference_id: referenceJsonLive.curie,
        mod_abbreviation: accessLevel,
        new_workflow_tag_atp_id: atpMap[action],
        transition_type: "manual",
      });

      console.log(`The manual indexing WFT has been successfully set to ${action}:`, response.data);
      addMessage(`The manual indexing WFT has been successfully set to ${action}.`, "success");
      dispatch(biblioQueryReferenceCurie(referenceJsonLive.curie));	// fetch referenceJsonLive from db after successful update
    } catch (error) {
      console.error(`Error setting WFT to ${action}:`, error);
      addMessage(`Failed to set WFT to ${action}.`, "danger");
    } finally {
      setTetDataLoading(false);
    }
  };

  const handleSpeciesSelection = (index, selectedSpecies) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[index].selectedSpecies = selectedSpecies;
      newRows[index].isSpeciesSelected = selectedSpecies.length > 0;  // Ensure this is updated correctly
      return newRows;
    });
  };

  // SCRUM-5697. The data_context a row will be saved with. Derived rather than
  // stored in row state for two reasons: an untouched row then follows the
  // entity field as it is filled in (WB splits on exactly that), and a late
  // accessLevel from redux cannot leave a stale default behind. Once the curator
  // picks a term, dataContextSelect holds it and wins.
  //
  // entityText is the display-time proxy for "has an entity"; the payload uses
  // the resolved entity curie instead, since that is what decides the shape of
  // the tag actually sent.
  //
  // SCRUM-6553. When the pulldown is hidden (FB) dataContextSelect is ignored,
  // not just unreachable: editing a tag preloads its stored data_context into
  // row state, and FB wants every save to carry the default regardless.
  const rowDataContext = (row) =>
    (!hidesDataContextPulldown(accessLevel) && row.dataContextSelect) ||
    defaultDataContext(accessLevel, Boolean(row.entityText));

  // Declared before initializeUpdateJson uses it. Both are only invoked from
  // event handlers, so the const is initialised by then either way.

  const handleRowChange = (index, field, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[index] = { ...newRows[index], [field]: value };
      const currentRow = newRows[index];
	
      if (field === 'entityResultList') {
        // make sure entityResultList is always treated as an array
        currentRow.entityResultList = Array.isArray(value) ? value : [];
      } else {
        currentRow[field] = value;
      }
	
      if (field === 'topicSelect') {
        currentRow.topicSelect = value || "";

        // auto-populate entity type for ALL rows (cloned or not)
        if (entityTypeList.includes(value)) {
          currentRow.entityTypeSelect = value;
        } else if (geneDescendants && geneDescendants.includes(value)) {
          currentRow.entityTypeSelect = "ATP:0000005"; // gene
        } else if (alleleDescendants && alleleDescendants.includes(value)) {
          currentRow.entityTypeSelect = "ATP:0000006"; // allele
        } else {
          currentRow.entityTypeSelect = "";
        }

        // Species topic: switch to autocomplete view and clear species field
        if (value === speciesATP) {
          currentRow.taxonSelect = "";
          currentRow.selectedSpecies = [];
        }

        // Clearing when topic is unset
        if (value === "" || value === null) {
          currentRow.newDataCheckbox = false;
          currentRow.newToDbCheckbox = false;
          currentRow.newToFieldCheckbox = false;
          currentRow.noDataCheckbox = false;
          currentRow.entityAdditionDoneCheckbox = false;
          currentRow.entityText = "";
          currentRow.entityResultList = [];
          currentRow.selectedSpecies = [];
        }
      }

      // Calculate disabledAddButton for the current row
      // Species is optional - tags can be created without species
      const disabledAddButton =
        (currentRow.topicSelect === speciesATP && !currentRow.isSpeciesSelected) ||
        !topicEntitySourceId ||
        !currentRow.topicSelect;

      currentRow.disabledAddButton = disabledAddButton;
	
      if (field === 'entityText') {
        inputRefs.current[index] = value; // store the current input value
      }

      if (field === 'selectedSpecies') {
        newRows[index].selectedSpecies = value; // store selected species
      }

      // Clear entity fields when species is set to empty (entities require species for validation)
      if (field === 'taxonSelect' && (value === "" || value === undefined)) {
        currentRow.entityText = "";
        currentRow.entityResultList = [];
      }

      // Validate the row when relevant fields change
      if (['entityText', 'taxonSelect', 'entityTypeSelect'].includes(field)) {
        handleEntityValidation(index, value);
      }
      newRows[index] = currentRow;
      return newRows;
    });
  };


  const handleSubmitAll = async () => {
    for (let index = 0; index < rows.length; index++) {
      await createEntities(referenceJsonLive.curie, index);
    }
    setRows([createNewRow()]);
  };

  const cloneRow = (index) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      const clonedRow = { ...newRows[index], isCloned: true };
      newRows.splice(index + 1, 0, clonedRow);
      return newRows;
    });
  };

  useEffect(() => {
    if (referenceJsonLive?.workflow_tags) {
      const uniqueMessages = new Set();
      referenceJsonLive.workflow_tags.forEach((tag) => {
        if (tag.updated_by !== uid) {
          if (tag.workflow_tag_id === "ATP:0000276") {
            uniqueMessages.add(
              `${tag.updated_by_name} has started adding the topic/entity tags for this paper.`
            );
          } else if (tag.workflow_tag_id === "ATP:0000275") {
            uniqueMessages.add(
              `${tag.updated_by_name} has completed adding the topic/entity tags for this paper.`
            );
          }
        }
      });
      uniqueMessages.forEach((msg) => addMessage(msg, "warning"));
    }
  }, [referenceJsonLive?.workflow_tags, uid]);

  function getDataNoveltyAtpArray(row) {
    const dataNoveltyAtpArray = [];
    if (row.newDataCheckbox) { dataNoveltyAtpArray.push('ATP:0000321'); }
    else if ( row.newToDbCheckbox || row.newToFieldCheckbox ) {
      if (row.newToDbCheckbox) { dataNoveltyAtpArray.push('ATP:0000228'); }
      if (row.newToFieldCheckbox) { dataNoveltyAtpArray.push('ATP:0000229'); } }
    else if (	// has a valid entity and the topic is the same as the entity_type
      row.topicSelectValue === curieToNameEntityType[row.entityTypeSelect] &&
      row.entityResultList?.some(
        entity =>
          !warnTypesEntityValidation.includes(entity.curie) &&
          entity.curie !== "duplicate"
      ) ) { dataNoveltyAtpArray.push('ATP:0000334'); }
    else { dataNoveltyAtpArray.push('ATP:0000335'); }
    return dataNoveltyAtpArray;
  }


  const updateCurationStatusToCurated = (row, refCurie) => {
    const curation_status_id = topicAtpToCurationStatus[row.topicSelect]?.curation_status_id;
    let subPath = "/curation_status/";
    let method = "PATCH";
    let json_data = { 'curation_status': 'ATP:0000239' };
    // SCRUM-6518: attribute this manual edit to the ABC curator source.
    if (topicEntitySourceId) { json_data["tag_source_id"] = topicEntitySourceId; }
    if (curation_status_id === 'new') {
      method = "POST";
      json_data["mod_abbreviation"] = accessLevel;
      json_data["topic"] = row.topicSelect;
      json_data["reference_curie"] = refCurie; }
    else {
      subPath = "/curation_status/" + curation_status_id; }
    console.log("subPath: ", subPath);
    console.log("method: ", method);
    console.log("json_data: ");
    console.log(json_data);

    try {
      return new Promise((resolve, reject) => {
        api.request({
          url: subPath,
          method,
          data: json_data,
        })
        .then((res) => {
          const isValid = isSuccess(res.status);
          if (!isValid) {
            const response_message = `error: ${subPath} : API status code ${res.status} for method ${method}`;
            reject(new Error(response_message));
          } else {
            resolve(res.data);
          }
        })
        .catch((err) => {
          setIsVisibleMessageFailureSetCurationStatusToCurated(true);
          const errorMessage = 'Error setting curation_status to curated for topic ' + row.topicSelectValue + ' ' + row.topicSelect + '<br/>' + err.message;
          setMessageFailureSetCurationStatusToCurated(prev => prev ? prev + '<br/>' + errorMessage : errorMessage);
        })
      });
    } catch (err) {
      // Error is already show in Row dependent on isVisibleMessageFailureSetCurationStatusToCurated, but you can log more if needed
      console.warn("Failed to update curation status", err);
    }
  };

  async function createEntities(refCurie, index) {
    const row = rows[index]
    if (!row.topicSelect) {
      return;
    }

    if (row.entityAdditionDoneCheckbox) { await updateCurationStatusToCurated(row, refCurie); }

    const forApiArray = [];
    const subPath = "topic_entity_tag/";
    const method = "POST";

    const dataNoveltyAtpArray = getDataNoveltyAtpArray(row);
    for (const dataNoveltyAtp of dataNoveltyAtpArray.values()) {
      if (row.entityResultList && row.entityResultList.length > 0) {
        for (const entityResult of row.entityResultList.values()) {
          if (!warnTypesEntityValidation.includes(entityResult.curie)) {
            let entityIdValidation = "alliance";
            if (row.taxonSelect === "use_wb" && row.taxonSelectWB && row.entityTypeSelect) {
              entityIdValidation = "WB";
            }
            let entityCurie = entityResult.curie;
            if (row.topicSelect === speciesATP) {
                entityCurie = entityResult;
            } 
            const updateJson = initializeUpdateJson(refCurie, row, entityCurie, entityIdValidation, dataNoveltyAtp)

            if (entityIdValidation === "WB") {
              updateJson["entity_id_validation"] = "WB";
              updateJson["species"] = row.taxonSelectWB;
            }

            // console.log("updateJson = " + JSON.stringify(updateJson, null, 2));
            forApiArray.push([null, subPath, updateJson, method]);
          }
        }
      } else {
        // Allow tags without species (species is optional)
        const updateJson = initializeUpdateJson(refCurie, row, null, null, dataNoveltyAtp);
        forApiArray.push([null, subPath, updateJson, method]);
      }
    }

    if (forApiArray.length === 0) {
      console.error("No valid data to submit.");
      return;
    }

    dispatch(setBiblioUpdatingEntityAdd(forApiArray.length));

    const result = await checkForExistingTags(forApiArray, accessLevel, dispatch,
					      updateButtonBiblioEntityAdd);
    if (result) {
      setTagExistingMessage(prev => prev ? prev + '<br/>' + result.html : result.html);
      setIsTagExistingMessageVisible(true);
      setExistingTagResponses(result.existingTagResponses);
    }

    setTypeaheadOptions([]);

    // remove the row after submission
    setRows((prevRows) => {
      const newRows = prevRows.filter((_, i) => i !== index);
      // if no rows are left, add an empty row
      return newRows.length === 0 ? [createNewRow()] : newRows;
    });
  }

  async function patchEntities(refCurie, index) {
    const row = rows[index];
    if (row.topicSelect === null) {
      return;
    }
    const subPath = "topic_entity_tag/" + editTag;
    const method = "PATCH";
    const entityResultList = row.entityResultList || [];
    const dataNoveltyAtpArray = getDataNoveltyAtpArray(row);
    if (entityResultList.length > 1) {
      console.error("Error processing entry: too many entities");
      dispatch({
        type: "UPDATE_BUTTON_BIBLIO_ENTITY_ADD",
        payload: {
          responseMessage: "Only one entity allowed on edit. Please create additional tags with the add function.",
          accessLevel: accessLevel,
        },
      });
    } else if (dataNoveltyAtpArray.length > 1) {
      console.error("Error processing entry: too many Data Novelty ATP values");
      dispatch({
        type: "UPDATE_BUTTON_BIBLIO_ENTITY_ADD",
        payload: {
          responseMessage: "Only one data novelty value allowed on edit. Please create additional tags with the add function.",
          accessLevel: accessLevel,
        },
      });
    } else {
      let entityResult = entityResultList[0];
      let updateJson = initializeUpdateJson(refCurie, row, null, null, dataNoveltyAtpArray[0]);
      delete updateJson["tag_source_id"];	// initializeUpdateJson populates this but API will fail if sent with PATCH request
      delete updateJson["reference_curie"];		// initializeUpdateJson populates this but API will fail if sent with PATCH request
      updateJson["entity_id_validation"] = (row.entityTypeSelect) === "" ? null : "alliance";
      updateJson["entity_type"] = (row.entityTypeSelect) === "" ? null : row.entityTypeSelect;
      updateJson["species"] = (row.taxonSelect) === "" ? null : row.taxonSelect;
      if (row.taxonSelect === "use_wb" && row.taxonSelectWB !== "" && row.taxonSelectWB !== undefined && row.entityTypeSelect !== "") {
        updateJson["entity_id_validation"] = "WB";
        updateJson["species"] = row.taxonSelectWB;
      }
      if (entityResult) {
        updateJson["entity"] = entityResult.curie;
      }
      // updated_by is stamped server-side from the authenticated user; the uid
      // here is the Cognito token sub, which is not a users.id (SCRUM-6459).
      let array = [accessToken, subPath, updateJson, method];
      dispatch(setBiblioUpdatingEntityAdd(1));
      await dispatch(updateButtonBiblioEntityAdd(array, accessLevel));

      setTypeaheadOptions([]);
      if (topicTypeaheadRef.current !== null) {
        topicTypeaheadRef.current.clear();
      }
      dispatch(setEditTag(null));
    }
  }

  return (
    <Container fluid>
      <RowDivider />
      {/* Messages Area */}
      <Row className="form-group row mb-3">
        <Col sm="12">
	  {messages
            .filter((msg, index, self) =>
              index === self.findIndex(m => m.text === msg.text)
            )
            .map((msg, idx) => (
              <Alert
                key={idx}
                variant={msg.variant}
                onClose={() => handleCloseMessage(idx)}
                dismissible
              >
                {msg.text}
              </Alert>
           ))}
        </Col>
      </Row>

      <ModalGeneric
        showGenericModal={entityModalText !== "" ? true : false}
        genericModalHeader="Entity Error"
        genericModalBody={entityModalText}
        onHideAction={() => dispatch(setEntityModalText(""))}
      />
      <RowDivider />
      {isTagExistingMessageVisible && tagExistingMessage && (
        <Row className="form-group row">
          <Col sm="12">
            <div className="alert alert-warning" role="alert">
              <div className="table-responsive" dangerouslySetInnerHTML={{ __html: tagExistingMessage }}></div>
              <Button variant="outline-secondary" size="sm" onClick={handleCloseTagExistingMessage}>
                Close
              </Button>
            </div>
          </Col>
        </Row>
      )}
      {isVisibleMessageFailureSetCurationStatusToCurated && messageFailureSetCurationStatusToCurated && (
        <Row className="form-group row">
          <Col sm="12">
            <div className="alert alert-warning" role="alert">
              <div className="table-responsive" dangerouslySetInnerHTML={{ __html: messageFailureSetCurationStatusToCurated }}></div>
              <Button variant="outline-secondary" size="sm" onClick={handleCloseMessageFailureSetCurationStatusToCurated}>
                Close
              </Button>
            </div>
          </Col>
        </Row>
      )}


      {/* Title and "No TET data" Button */}
      <Row className="form-group row mb-3" style={{ alignItems: "center" }}>
        <Col sm="12" style={{ display: "flex", alignItems: "center" }}>
          <h3 style={{ marginRight: "4px" }}>Entity and Topic Addition</h3>
          <HelpDocLink url={HELP_DOC_URLS.topicEntityTagging} title="Open the Topic and Entity Tagging help documentation" style={{ marginRight: "10px" }} />

          {["inProgress", "complete"].map((statusKey) => {
            const workflowTagId = statusKey === "inProgress" ? "ATP:0000276" : "ATP:0000275";
            const isTagged = referenceJsonLive?.workflow_tags?.some(
              (tag) => tag.mod_abbreviation === accessLevel && tag.workflow_tag_id === workflowTagId
            );

            const buttonText = statusKey === "inProgress"
              ? "In progress adding TET data"
              : "Done adding TET / No TET data";

            const disabledText = statusKey === "inProgress"
              ? "Is In Progress adding TET"
              : "Is Done adding TET";

            return (
              <Button
                key={statusKey}
                variant={isTagged ? "outline-secondary" : "outline-primary"}
                size="sm"
                disabled={isTagged || tetDataLoading}
                onClick={!isTagged ? () => handleTetDataClick(statusKey) : undefined}
                style={{ marginRight: statusKey === "inProgress" ? "10px" : undefined }}
              >
                {tetDataLoading && !isTagged ? (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                ) : (
                  isTagged ? disabledText : buttonText
                )}
              </Button>
            );
          })}

          <Button
            variant="outline-primary"
            size="sm"
            style={{ marginLeft: "10px" }}
            onClick={() => dispatch(changeBiblioActionToggler({ target: { id: 'biblio-toggler-quicktopic' } }, 'quicktopic'))}
          >
            Quick Topic Addition
          </Button>
        </Col>
      </Row>


      <Row className="form-group row">
        <Col className="div-grey-border" sm="2">
          topic
        </Col>
        <Col className="div-grey-border" sm="1">
          data context / novelty
        </Col>
        <Col className="div-grey-border" sm="1">
          entity type
        </Col>
        <Col className="div-grey-border" sm="1">
          species
        </Col>
        <Col className="div-grey-border" sm="2">
          entity list (one per line, case insensitive)
        </Col>
        <Col className="div-grey-border" sm="2">
          entity validation
        </Col>
        <Col className="div-grey-border" sm="2">
          internal notes
        </Col>
        <Col className="div-grey-border" sm="1">
          button
        </Col>
      </Row>
      {rows.map((row, index) => {
        const hasBlockingEntity =
          row.topicSelectValue === curieToNameEntityType[row.entityTypeSelect] &&
          row.entityResultList?.some(entity => !warnTypesEntityValidation.includes(entity.curie) && entity.curie !== "duplicate");

        // Derive effective checkbox states - if blocked, always show as unchecked
        const effectiveNewDataCheckbox = hasBlockingEntity ? false : row.newDataCheckbox;
        const effectiveNewToDbCheckbox = hasBlockingEntity ? false : row.newToDbCheckbox;
        const effectiveNewToFieldCheckbox = hasBlockingEntity ? false : row.newToFieldCheckbox;

        return (
          <React.Fragment key={index}>
            <Row className="form-group row" style={{ marginBottom: '15px' }}>
              <Col sm="2">
                <AsyncTypeahead
                  isLoading={topicSelectLoading}
                  useCache={false}
                  delay={300}
                  placeholder="Start typing to search topics"
                  ref={topicTypeaheadRef}
                  id={`topicTypeahead-${index}`}
                  onSearch={(query) => runTopicSearch(
                    (signal) => {
                      let url = `/ontology/search_topic/${encodeURIComponent(query)}`;
                      if (accessLevel) {
                        url += "?mod_abbr=" + accessLevel
                      }
                      return api.get(url, { signal });
                    },
                    (response, err) => {
                      if (err) {
                        console.error("Error during topic search:", err);
                        setTypeaheadOptions([]);
                        return;
                      }
                      const results = response.data;
                      if (!Array.isArray(results)) {
                        console.error("Error during topic search: invalid response format");
                        setTypeaheadOptions([]);
                        return;
                      }
                      const nameToCurie = {};
                      for (const item of results) {
                        nameToCurie[item.name] = item.curie;
                      }
                      dispatch(setTypeaheadName2CurieMap(nameToCurie));
                      setTypeaheadOptions(Object.keys(nameToCurie));
                    },
                    setTopicSelectLoading,
                  )}
                  onChange={(selected) => {
                    if (selected.length > 0) {
                      const selectedCurie = typeaheadName2CurieMap[selected[0]];
          	    const selectedValue = selected[0];
                      handleRowChange(index, 'topicSelect', selectedCurie);
          	      handleRowChange(index, 'topicSelectValue', selectedValue);
                    } else {
          	      handleRowChange(index, 'topicSelect', '');
          	      handleRowChange(index, 'topicSelectValue', '');
          	    }
                  }}
                  options={typeaheadOptions}
                  selected={row.topicSelectValue ? [row.topicSelectValue] : []}	
                />		
              </Col>
              <Col sm="1">
                <div style={{ textAlign: "left" }}>
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`newDataCheckbox-${index}`}
                    checked={effectiveNewDataCheckbox}
                    disabled={ row.newToDbCheckbox || row.newToFieldCheckbox || row.noDataCheckbox || hasBlockingEntity }
                    onChange={(evt) => {
                      const updatedRows = [...rows];
                      updatedRows[index] = { ...updatedRows[index], newDataCheckbox: evt.target.checked };
                      setRows(updatedRows);
                    }}
                  />
                  <span style={{ color: row.newToDbCheckbox || row.newToFieldCheckbox || row.noDataCheckbox || hasBlockingEntity ? 'gray' : 'inherit', }} >New Data</span>
                  <br />
                  <span style={{ display: 'inline-block', width: '1rem' }} />
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`newToDbCheckbox-${index}`}
                    checked={effectiveNewToDbCheckbox}
                    disabled={ row.newDataCheckbox || row.noDataCheckbox || hasBlockingEntity || (editTag && row.newToFieldCheckbox) }
                    onChange={(evt) => {
                      const updatedRows = [...rows];
                      updatedRows[index] = { ...updatedRows[index], newToDbCheckbox: evt.target.checked };
                      setRows(updatedRows);
                    }}
                  />
                  <span style={{ color: row.newDataCheckbox || row.noDataCheckbox || hasBlockingEntity || (editTag && row.newToFieldCheckbox) ? 'gray' : 'inherit', }} >New to DB</span>
                  <br />
                  <span style={{ display: 'inline-block', width: '1rem' }} />
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`newToFieldCheckbox-${index}`}
                    checked={effectiveNewToFieldCheckbox}
                    disabled={ row.newDataCheckbox || row.noDataCheckbox || hasBlockingEntity || (editTag && row.newToDbCheckbox) }
                    onChange={(evt) => {
                      const updatedRows = [...rows];
                      updatedRows[index] = { ...updatedRows[index], newToFieldCheckbox: evt.target.checked };
                      setRows(updatedRows);
                    }}
                  />
                  <span style={{ color: row.newDataCheckbox || row.noDataCheckbox || hasBlockingEntity || (editTag && row.newToDbCheckbox) ? 'gray' : 'inherit', }} >New to Field</span>
                  <br />
                  <Form.Check
                    inline
                    type="checkbox"
                    id={`noDataCheckbox-${index}`}
                    checked={row.noDataCheckbox}
                    disabled={ row.newToDbCheckbox || row.newToFieldCheckbox || row.newDataCheckbox }
                    onChange={(evt) => {
                      const updatedRows = [...rows];
                      updatedRows[index] = { ...updatedRows[index], noDataCheckbox: evt.target.checked };
                      setRows(updatedRows);
                    }}
                  />
                  <span style={{ color: row.newToDbCheckbox || row.newToFieldCheckbox || row.newDataCheckbox ? 'gray' : 'inherit', }} >No Data</span>
                  {/* SCRUM-6553. FB never sees the pulldown; rowDataContext keeps
                      sending their default (experimentally studied) regardless. */}
                  {!hidesDataContextPulldown(accessLevel) && (
                  <Form.Control
                    as="select"
                    size="sm"
                    id={`dataContextSelect-${index}`}
                    title="Data context"
                    style={{ marginTop: '6px', fontSize: '0.75rem' }}
                    value={rowDataContext(row)}
                    onChange={(e) => handleRowChange(index, 'dataContextSelect', e.target.value)}
                  >
                    {/* The effective term is not always one the dropdown offers.
                        A pipeline-written tag can carry a grouping or the root --
                        the WB entity-extraction models send ATP:0000323 -- and a
                        WB topic-only row now *defaults* to that root, since only
                        leaves are selectable. Without a matching <option> the
                        browser would display the first one instead, and saving
                        would silently rewrite the stored value. Show it as a
                        disabled option so it renders truthfully but cannot be
                        chosen. */}
                    {rowDataContext(row) &&
                      !dataContextOptions.some((o) => o.curie === rowDataContext(row)) && (
                      <option key={rowDataContext(row)} value={rowDataContext(row)} disabled>
                        {dataContextNameMap[rowDataContext(row)] || rowDataContext(row)}
                      </option>
                    )}
                    {dataContextOptions.map((option) => (
                      <option key={option.curie} value={option.curie}>
                        {option.name}
                      </option>
                    ))}
                  </Form.Control>
                  )}
                </div>
              </Col>
              <Col sm="1">
                <Form.Control
                  as="select"
                  id={`entityTypeSelect-${index}`}
                  type="entityTypeSelect"
                  value={row.entityTypeSelect}
                  onChange={(e) => handleRowChange(index, 'entityTypeSelect', e.target.value)}
                >
                  {entityTypeList.map((option, idx) => (
                    <option key={idx} value={option}>
                      {curieToNameEntityType[option]}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col sm="1">
                <Form.Control
                  as="select"
                  id={`taxonSelect-${index}`}
                  type="taxonSelect"
                  value={row.taxonSelect}
                  onChange={(e) => handleRowChange(index, 'taxonSelect', e.target.value)}
                >
                  {taxonList.map((option, idx) => (
                    <option key={idx} value={option}>
                      {option === "" ? "(No species)" : curieToNameTaxon[option]}
                    </option>
                  ))}
                </Form.Control>

                {row.taxonSelect === "use_wb" && (
                  <Form.Control
                    as="select"
                    id={`taxonSelectWB-${index}`}
                    type="taxonSelectWB"
                    value={row.taxonSelectWB}
                    onChange={(e) => handleRowChange(index, 'taxonSelectWB', e.target.value)}
                  >
                    {taxonListWB.map((option, idx) => (
                      <option key={idx} value={option}>
                         {curieToNameTaxonWB[option]}
                      </option>
          	  ))}
                  </Form.Control>	
                )}
              </Col>
              <Col className="form-label col-form-label" sm="2">
                {renderView(row) === "autocomplete" ? (
                  <AsyncTypeahead
          	  id={`species-typeahead-${index}`}
                    multiple
                    isLoading={speciesSelectLoading}
                    delay={300}
                    placeholder="enter species name"
                    ref={speciesTypeaheadRef}
                    onSearch={(query) => runSpeciesSearch(
                      (signal) => api.get(`/ontology/search_species/${encodeURIComponent(query)}`, { signal }),
                      (response, err) => {
                        if (err) {
                          console.error("Error fetching typeahead options:", err);
                          return;
                        }
                        const results = response.data;
                        if (results) {
                          setTypeaheadOptions(results.map((item) => `${item.name} ${item.curie}`));
                        }
                      },
                      setSpeciesSelectLoading,
                    )}
                    onChange={(selected) => {
                      const extractedStrings = selected
                        .map((specie) => {
                          const match = specie.match(/(.+) (NCBITaxon:\d+)/);
          		return match ? `${match[2]}` : null;
                        })
                        .filter((item) => item);

          	      handleSpeciesSelection(index, extractedStrings);
          	      handleRowChange(index, 'entityResultList', extractedStrings);
                        handleRowChange(index, 'selectedSpecies', selected.map(s => s));
                    }}
                    options={typeaheadOptions}
          	  selected={row.selectedSpecies || row.entityResultList.map(entity => `${entity.entityTypeSymbol} ${entity.curie}`)}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    id={`entitytextarea-${index}`}
                    value={row.entityText}
                    disabled={!row.taxonSelect || row.taxonSelect === ""}
                    placeholder={!row.taxonSelect || row.taxonSelect === "" ? "Select species to add entities" : ""}
                    onChange={(e) => handleRowChange(index, 'entityText', e.target.value)}
                  />
                )}
              </Col>
              <Col className="form-label col-form-label" sm="2">
                <Container>
                  {renderView(row) === "list" &&
                    row.entityResultList &&
                    row.entityResultList.length > 0 &&
                    row.entityResultList.map((entityResult, idx) => {
                      let colDisplayClass = "Col-display";
                        if (warnTypesEntityValidation.includes(entityResult.curie)) {
                        colDisplayClass = "Col-display-warn";
                      } else if (entityResult.curie === "duplicate") {
                        colDisplayClass = "Col-display-grey";
                      }
                      return (
                        <Row key={`entityEntityContainerrows-${idx}`}>
                          <Col className={`Col-general ${colDisplayClass} Col-display-left`} sm="5">
                            {entityResult.entityTypeSymbol}
                          </Col>
                          <Col className={`Col-general ${colDisplayClass} Col-display-right`} sm="7">
                            {entityResult.curie}
                          </Col>
                        </Row>
                      );
                    })}
                </Container>
              </Col>
              <Col className="form-label col-form-label" sm="2">
                <Form.Control as="textarea" id={`notetextarea-${index}`} type="notetextarea" value={row.noteText} onChange={(e) => {
                  const updatedRows = [...rows];
                  updatedRows[index] = { ...updatedRows[index], noteText: e.target.value };
                  setRows(updatedRows);
                }} />
              </Col>
              <Col className="form-label col-form-label" sm="1">
                {editTag ? (
                  <Button variant="outline-danger" onClick={() => patchEntities(referenceJsonLive.curie, index)}>
                    {biblioUpdatingEntityAdd > 0 ? <Spinner animation="border" size="sm" /> : "Edit"}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline-primary" disabled={row.disabledAddButton} onClick={() => createEntities(referenceJsonLive.curie, index)}>
                      {biblioUpdatingEntityAdd > 0 ? <Spinner animation="border" size="sm" /> : "Submit"}
                    </Button>
                    { (row.topicSelect !== '') && (row.topicSelect === row.entityTypeSelect) && (
                      <>
                        <br/>
                        <Form.Check
                          inline
                          type="checkbox"
                          id={`topic_entity_addition_done-${index}`}
                          checked={row.entityAdditionDoneCheckbox}
                          onChange={(evt) => {
                            const updatedRows = [...rows];
                            updatedRows[index] = { ...updatedRows[index], entityAdditionDoneCheckbox: evt.target.checked };
                            setRows(updatedRows);
                          }}
                        />
                        <span>Entity Addition Done</span>
                        {debugMode && (<span style={{ color: 'red', }} > {topicAtpToCurationStatus[row.topicSelect]?.curation_status} {topicAtpToCurationStatus[row.topicSelect]?.curation_status_id}</span>)}
                      </>) }
                  </>
                )}
              </Col>
            </Row>
            { (editTag === null) && (
              <Row className="mb-3" style={{ marginBottom: '20px' }}>
                <Col sm="6" className="d-flex align-items-center">
                  <Button variant="outline-secondary"  onClick={() => cloneRow(index)} style={{ marginRight: '10px' }}>
                    Clone row
                  </Button>
                  {rows.length - 1 === index && (
                    <Button variant="outline-secondary"  onClick={() => setRows([...rows, createNewRow()])}>
                      New row
                    </Button>
                  )}
                </Col>
                {rows.length - 1 === index && rows.length > 1 && (
                  <Col sm="6" className="d-flex justify-content-end align-items-center">
                    <Button variant="outline-primary" onClick={handleSubmitAll}>
                      Submit All
                    </Button>
                  </Col>
                )}
              </Row>
            )}
          </React.Fragment>
      )})}
    </Container>
  );
};

export default TopicEntityCreate;
